<template>
  <b-modal size="lg" :visible="visible" no-close-on-backdrop no-close-on-esc centered>
    <div slot="modal-header" class="w-100">
      <h5 class="pt-2">{{ title}}
        <button type="button" class="close"
                @click.prevent="closeModal()">
          <span aria-hidden="true">&times;</span>
        </button>
      </h5>
    </div>
    <div class="container-fluid">
      <div class="row justify-content-center m-2">
        <div class="alert alert-danger col" role="alert" v-show="generalErrorMessage">
          <div>{{ generalErrorMessage }}</div>
        </div>
      </div>
      <div class="row">
        <div class="col-2">
        </div>
        <div class="col-7">
          <b-form-group :label-cols="4" label="First Name" label-align="right" :state="stateForField('firstName')" :invalid-feedback="errorForField('firstName')">
            <b-form-input v-model="patient.organizationPatientDemographic.firstName"></b-form-input>
          </b-form-group>
          <b-form-group :label-cols="4" label="Last Name" label-align="right" :state="stateForField('lastName')" :invalid-feedback="errorForField('lastName')">
            <b-form-input v-model="patient.organizationPatientDemographic.lastName"></b-form-input>
          </b-form-group>
          <b-form-group label-cols="4"
                        label="Birth Date"
                        label-class="text-right"
                        :invalid-feedback="errorForField('birthDate')"
                        :state="stateForField('birthDate')">
            <datepicker format="MM/dd/yyyy" :use-utc="true" :typeable="true" class="form-control datepicker" v-model="patient.organizationPatientDemographic.birthDate"></datepicker>
          </b-form-group>
          <b-form-group
              :label-cols="4"
              :state="stateForField('genderId')"
              :invalid-feedback="errorForField('genderId')"
              label="Gender"
              label-class="text-right">
            <b-form-radio-group v-model="patient.organizationPatientDemographic.genderId" class="pt-2" :options="genderOptions" />
          </b-form-group>
          <b-form-group :label-cols="4" label="Email" label-align="right" :state="stateForField('email')" :invalid-feedback="errorForField('email')">
            <b-form-input v-model="patient.organizationPatientDemographic.email"></b-form-input>
          </b-form-group>
          <b-form-group :label-cols="4" label="Mobile Phone" label-align="right" :state="stateForField('mobilePhone')" :invalid-feedback="errorForField('mobilePhone')">
            <b-form-input @input="numberInput" :value="phoneDisplay"></b-form-input>
          </b-form-group>
        </div>
      </div>
    </div>
    <div slot="modal-footer" class="w-100">
      <button type="button" class="btn btn-orange float-right" @click.prevent="savePatient()"><font-awesome-icon size="lg" class="mr-2" v-show="isSaving" icon="spinner"  pulse/>Add Patient</button>
      <button type="button" class="btn btn-outline-orange mr-2 float-right" @click.prevent="closeModal()">Close</button>
    </div>
  </b-modal>
</template>

<script>
  import ErrorsMixin from '../mixins/ErrorsMixin'
  import Datepicker from 'vuejs-datepicker'
  import api from '@/services/api'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'

  export default {
    name: 'NewPatientModal',

    components: { Datepicker },

    mixins: [ErrorsMixin, AnalyticsMixin],

    props: {
      title: {
        type: String,
        default: 'Add New Patient'
      },
      visible: {
        type: Boolean,
        required: true
      }
    },

    data () {
      return {
        patient: {
          organizationPatientDemographic: {
            birthDate: '',
            firstName: '',
            lastName: '',
            genderId: 0,
            email: ''
          },
          organizationPatientPhoneNumbers: [
            {
              area: '',
              exchange: '',
              number: ''
            }
          ]
        },
        isSaving: false
      }
    },

    watch: {
      visible (newValue, oldValue) {
        if (newValue) {
          this.clearData()
        }
      }
    },

    computed: {
      genderOptions () {
        return this.$store.genderOptions
      },

      phoneDisplay () {
        if (this.patient.organizationPatientPhoneNumbers[0].area) {
          if (this.patient.organizationPatientPhoneNumbers[0].number.length > 0) {
            return '(' + this.patient.organizationPatientPhoneNumbers[0].area + ') ' + this.patient.organizationPatientPhoneNumbers[0].exchange + '-' + this.patient.organizationPatientPhoneNumbers[0].number
          } else if (this.patient.organizationPatientPhoneNumbers[0].exchange.length > 0) {
            return '(' + this.patient.organizationPatientPhoneNumbers[0].area + ') ' + this.patient.organizationPatientPhoneNumbers[0].exchange
          } else if (this.patient.organizationPatientPhoneNumbers[0].area.length > 0) {
            return '(' + this.patient.organizationPatientPhoneNumbers[0].area
          }
        }
        return ''
      }
    },

    methods: {
      closeModal () {
        this.trackEvent('Insurance', 'EditModal', 'Closed')
        this.$emit('update:visible', false)
      },

      validate () {
        this.clearAllErrors()

        if (!this.patient.organizationPatientDemographic.firstName) {
          this.addErrorForField('firstName', 'You must add a first name')
        }

        if (!this.patient.organizationPatientDemographic.lastName) {
          this.addErrorForField('lastName', 'You must add a last name')
        }

        if (this.patient.organizationPatientDemographic.birthDate === null) {
          this.addErrorForField('birthDate', 'You must add a date of birth')
        }

        if (!this.patient.organizationPatientDemographic.genderId > 0) {
          this.addErrorForField('genderId', 'You must select a gender')
        }

        if (!this.patient.organizationPatientDemographic.email) {
          this.addErrorForField('email', 'You must add an email address')
        }

        if (!this.phoneDisplay > 0) {
          this.addErrorForField('mobilePhone', 'You must add a phone number')
        }

        return !this.hasErrors()
      },

      clearData () {
        this.clearAllErrors()
        this.patient = {
          organizationPatientDemographic: {
            birthDate: '',
            firstName: '',
            lastName: '',
            genderId: 0,
            email: ''
          },
          organizationPatientPhoneNumbers: [
            {
              area: '',
              exchange: '',
              number: '',
              phoneType: 3
            }
          ]
        }
      },

      savePatient () {
        if (this.isSaving) {
          return
        }

        if (!this.validate()) {
          return
        }

        this.isSaving = true
        this.errorMessage = ''

        api.post('/Dashboard/Patient/Create', this.patient).then(response => {
          this.$emit('newPatientAdded', response.data)

        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
          }
        }).then(() => {
          this.isSaving = false
        })
      },

      numberInput (value) {
        const newNumber = value
        let cleanNumber = newNumber.replace(/\D/g, '')
        if (cleanNumber.length > 0) {
          if (parseInt(cleanNumber[0]) === 1) {
            cleanNumber = cleanNumber.substr(1, cleanNumber.length - 1)
          }

          this.patient.organizationPatientPhoneNumbers[0].area = cleanNumber.substr(0, cleanNumber.length > 3 ? 3 : cleanNumber.length)
          if (cleanNumber.length > 3) {
            this.patient.organizationPatientPhoneNumbers[0].exchange = cleanNumber.substr(3, cleanNumber.length > 6 ? 3 : cleanNumber.length - 3)
          } else {
            this.patient.organizationPatientPhoneNumbers[0].exchange = ''
          }
          if (cleanNumber.length > 6) {
            this.patient.organizationPatientPhoneNumbers[0].number = cleanNumber.substr(6, cleanNumber.length < 10 ? cleanNumber.length - 6 : 4)
          } else {
            this.patient.organizationPatientPhoneNumbers[0].number = ''
          }
        } else {
          this.patient.organizationPatientPhoneNumbers[0].area = ''
          this.patient.organizationPatientPhoneNumbers[0].exchange = ''
          this.patient.organizationPatientPhoneNumbers[0].number = ''
        }

      }
    }
  }
</script>

<style>

</style>
